// src/pages/CancerHub.jsx
// Cancer Treatment & Clinical Guides Hub Page

import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { trackPageView, trackScrollDepth, trackCTA } from '@/utils/seo/analytics';
import { cancerPagesData } from '@/data/cancerPagesData';
import { SITE_ORIGIN } from '@/config/site';
import BrandLogo from '@/components/brand/BrandLogo';
import Footer from '@/components/Footer';
import { 
  Stethoscope, 
  ArrowRight,
  Building2,
  Calculator,
  ShieldCheck,
  Search,
  Sparkles,
  Activity,
  HeartPulse,
  ChevronRight,
  X,
  FileText,
  BadgeCheck,
  CheckCircle2,
  Info
} from 'lucide-react';
import '@/App.css';

// Specialized classification metadata for rich clinical badges & filtering
const CANCER_METADATA_MAP = {
  breast: {
    category: "Women's Oncology",
    badge: "High Incidence",
    modalities: ["Surgery", "Chemo", "Radiation", "Targeted", "Hormone"],
    organs: "Breast tissue, axillary lymph nodes",
    cureRateBadge: "High early cure rate",
  },
  lung: {
    category: "Thoracic & Head-Neck",
    badge: "Leading Cause",
    modalities: ["Targeted Therapy", "Immunotherapy", "Surgery", "Radiation"],
    organs: "Lungs, bronchi, mediastinum",
    cureRateBadge: "EGFR/ALK targeted breakthroughs",
  },
  oral: {
    category: "Thoracic & Head-Neck",
    badge: "High Prevalence in India",
    modalities: ["Onco-Surgery", "Radiation", "Reconstruction", "Chemo"],
    organs: "Tongue, buccal mucosa, lips, throat",
    cureRateBadge: "Screening detectable",
  },
  cervical: {
    category: "Women's Oncology",
    badge: "Preventable & Treatable",
    modalities: ["Chemoradiation", "Brachytherapy", "Radical Hysterectomy"],
    organs: "Cervix, pelvis",
    cureRateBadge: "HPV vaccine preventable",
  },
  colorectal: {
    category: "Gastrointestinal",
    badge: "Early Detection Curable",
    modalities: ["Laparoscopic Surgery", "Chemo", "Targeted (anti-VEGF/EGFR)"],
    organs: "Colon, rectum",
    cureRateBadge: "90% survival when localized",
  },
  prostate: {
    category: "Men's Oncology",
    badge: "High Survival Rate",
    modalities: ["Robotic Surgery", "Radiation Therapy", "Androgen Deprivation"],
    organs: "Prostate gland",
    cureRateBadge: "Nearly 99% localized survival",
  },
  ovarian: {
    category: "Women's Oncology",
    badge: "Specialized Debulking",
    modalities: ["Cytoreductive Surgery", "HIPEC", "Platinum Chemo", "PARP Inhibitors"],
    organs: "Ovaries, peritoneum",
    cureRateBadge: "Subspecialist surgery critical",
  },
  'liver-hcc': {
    category: "Gastrointestinal",
    badge: "Multidisciplinary Care",
    modalities: ["Surgical Resection", "TACE / RFA", "Immunotherapy", "Liver Transplant"],
    organs: "Liver parenchyma",
    cureRateBadge: "Transplant & ablation options",
  },
  pancreatic: {
    category: "Gastrointestinal",
    badge: "Expert Whipple Required",
    modalities: ["Whipple Resection", "FOLFIRINOX Chemo", "Targeted / Radiotherapy"],
    organs: "Pancreas (head/body/tail)",
    cureRateBadge: "High-volume center recommended",
  },
  'non-hodgkin-lymphoma': {
    category: "Hematologic",
    badge: "High Response Rate",
    modalities: ["Immunochemotherapy (R-CHOP)", "Targeted Therapy", "CAR-T Cell"],
    organs: "Lymphatic system, spleen, bone marrow",
    cureRateBadge: "Many subtypes highly curable",
  },
};

const CATEGORIES = [
  "All Types",
  "Women's Oncology",
  "Gastrointestinal",
  "Thoracic & Head-Neck",
  "Men's Oncology",
  "Hematologic"
];

export default function CancerHub() {
  const navigate = useNavigate();
  const baseUrl = SITE_ORIGIN;
  const pageUrl = `${baseUrl}/cancer`;

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All Types');

  useEffect(() => {
    trackPageView(pageUrl, "Cancer Types - Treatment Information | ByOnco");
    trackScrollDepth();
  }, [pageUrl]);

  // Aggregate all cancer types data with enriched clinical tags
  const cancerTypes = useMemo(() => {
    return Object.values(cancerPagesData).map(cancer => {
      const meta = CANCER_METADATA_MAP[cancer.slug] || {
        category: "General Oncology",
        badge: "Specialized Guide",
        modalities: ["Surgery", "Chemotherapy", "Radiation"],
        organs: "Multiple sites",
        cureRateBadge: "Evidence-based",
      };

      return {
        name: cancer.name,
        slug: cancer.slug,
        description: cancer.content.intro,
        shortDescription: cancer.content.intro.length > 155 
          ? cancer.content.intro.substring(0, 152).trim() + '...'
          : cancer.content.intro,
        meta: cancer.meta,
        category: meta.category,
        badge: meta.badge,
        modalities: meta.modalities,
        organs: meta.organs,
        cureRateBadge: meta.cureRateBadge,
      };
    });
  }, []);

  // Filter based on search query and active category chip
  const filteredCancers = useMemo(() => {
    return cancerTypes.filter(cancer => {
      const matchesCategory = 
        selectedCategory === 'All Types' || cancer.category === selectedCategory;

      const normalizedSearch = searchQuery.trim().toLowerCase();
      const matchesSearch = 
        !normalizedSearch ||
        cancer.name.toLowerCase().includes(normalizedSearch) ||
        cancer.description.toLowerCase().includes(normalizedSearch) ||
        cancer.category.toLowerCase().includes(normalizedSearch) ||
        cancer.modalities.some(m => m.toLowerCase().includes(normalizedSearch));

      return matchesCategory && matchesSearch;
    });
  }, [cancerTypes, selectedCategory, searchQuery]);

  // Generate JSON-LD Schema
  const generateSchema = () => {
    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": baseUrl
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Cancer Types",
          "item": pageUrl
        }
      ]
    };

    const itemListSchema = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": "Cancer Types - Treatment Information",
      "description": "Comprehensive information about different types of cancer, including symptoms, diagnosis, treatment options, and costs for India and US.",
      "numberOfItems": cancerTypes.length,
      "itemListElement": cancerTypes.map((cancer, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": cancer.name,
        "url": `${baseUrl}/cancer/${cancer.slug}`,
        "description": cancer.shortDescription
      }))
    };

    const organizationSchema = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "ByOnco",
      "url": baseUrl,
      "logo": `${baseUrl}/byonco-logo.png`,
      "description": "Cancer care navigation that helps patients find the right centers"
    };

    return [breadcrumbSchema, itemListSchema, organizationSchema];
  };

  const schemas = generateSchema();

  return (
    <>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>Cancer Types - Treatment Information | ByOnco</title>
        <meta name="title" content="Cancer Types - Treatment Information | ByOnco" />
        <meta name="description" content="Comprehensive cancer treatment information for 10+ cancer types. Compare symptoms, diagnosis, treatment options, costs, and find the best hospitals in India and US." />
        <link rel="canonical" href={pageUrl} />

        {/* Hreflang Tags */}
        <link rel="alternate" hreflang="en-IN" href={`${pageUrl}?locale=en-IN`} />
        <link rel="alternate" hreflang="en-US" href={`${pageUrl}?locale=en-US`} />
        <link rel="alternate" hreflang="x-default" href={pageUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content="Cancer Types - Treatment Information | ByOnco" />
        <meta property="og:description" content="Comprehensive cancer treatment information for 10+ cancer types. Compare symptoms, diagnosis, treatment options, costs, and find the best hospitals." />
        <meta property="og:image" content={`${SITE_ORIGIN}/preview.png`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="ByOnco - Guided Cancer Care" />
        <meta property="og:site_name" content="ByOnco" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={pageUrl} />
        <meta name="twitter:title" content="Cancer Types - Treatment Information | ByOnco" />
        <meta name="twitter:description" content="Comprehensive cancer treatment information for 10+ cancer types. Compare symptoms, diagnosis, treatment options, costs, and find the best hospitals." />
        <meta name="twitter:image" content={`${SITE_ORIGIN}/preview.png`} />
        <meta name="twitter:image:alt" content="ByOnco - Guided Cancer Care" />

        {/* Additional SEO */}
        <meta name="robots" content="index, follow" />
        <meta name="language" content="English" />
        <meta name="author" content="PraesidioCare Private Limited" />

        {/* JSON-LD Schemas */}
        {schemas.map((schema, index) => (
          <script
            key={index}
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
          />
        ))}
      </Helmet>

      {/* Main Page Canvas: Soothing Warm Cream & Clean Clinical Typography */}
      <div className="min-h-screen bg-[#FBFBFA] text-slate-900 font-sans antialiased selection:bg-emerald-100 selection:text-emerald-900">
        
        {/* Sticky Translucent Header with Transparent Logo */}
        <header className="bg-white/85 backdrop-blur-md border-b border-slate-200/80 sticky top-0 z-50 transition-all">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <BrandLogo size="md" />
              <div className="hidden md:flex items-center space-x-2 text-xs font-medium text-slate-500">
                <span className="text-slate-300">/</span>
                <span className="text-emerald-950 font-semibold">Clinical Guides Directory</span>
              </div>
            </div>

            {/* Quick Header Nav & High-Converting CTA */}
            <div className="flex items-center gap-3">
              <Link
                to="/find-hospitals"
                className="hidden sm:inline-flex text-xs font-medium text-slate-600 hover:text-forest-ink transition"
              >
                Find Hospitals
              </Link>
              <Link
                to="/cost-calculator"
                className="hidden sm:inline-flex text-xs font-medium text-slate-600 hover:text-forest-ink transition"
              >
                Cost Calculator
              </Link>
              <Link
                to="/second-opinion"
                className="hidden sm:inline-flex text-xs font-medium text-slate-600 hover:text-forest-ink transition"
              >
                Second Opinion
              </Link>
              <Link
                to="/get-started"
                onClick={() => trackCTA('get_matched', 'header_button', { page: '/cancer' })}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-forest-ink hover:bg-forest-shadow text-white text-xs sm:text-sm font-semibold transition shadow-xs hover:shadow"
              >
                <span>Get Matched Care</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </header>

        {/* Hero Section with Ambient Gradient Glow */}
        <section className="relative pt-12 pb-16 overflow-hidden bg-gradient-to-b from-emerald-50/70 via-[#FBFBFA] to-[#FBFBFA] border-b border-slate-200/70">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
            
            {/* Trust Pill */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-100/80 border border-emerald-200 text-emerald-900 text-xs sm:text-sm font-medium mb-6 shadow-xs"
            >
              <ShieldCheck className="w-4 h-4 text-emerald-700 flex-shrink-0" />
              <span>NCCN Guideline Aligned · Multidisciplinary Review · India & Global Centers</span>
            </motion.div>

            {/* Main Title */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-3xl sm:text-5xl lg:text-6xl font-bold font-serif text-forest-ink tracking-tight mb-5 leading-tight"
            >
              Cancer Treatment & Clinical Guides
            </motion.h1>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-base sm:text-lg lg:text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed mb-10"
            >
              Authoritative, expert-reviewed medical guides on diagnosis, staging, breakthrough therapies, and hospital treatment cost benchmarks to help patients and families navigate with confidence.
            </motion.p>

            {/* Pathway Tiles (Replacing the old stacked full-width button rows) */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto text-left mb-6"
            >
              {/* Tile 1: Find Hospitals */}
              <Link
                to="/find-hospitals"
                onClick={() => trackCTA('find_hospitals', 'hero_tile', { page: '/cancer' })}
                className="group p-4 sm:p-5 rounded-2xl bg-white border border-slate-200/90 shadow-xs hover:shadow-md hover:border-emerald-600/40 hover:-translate-y-0.5 transition-all flex flex-col justify-between"
              >
                <div>
                  <div className="w-10 h-10 rounded-xl bg-emerald-50 text-forest-ink flex items-center justify-center mb-3 group-hover:bg-forest-ink group-hover:text-white transition-colors">
                    <Building2 className="w-5 h-5" />
                  </div>
                  <h2 className="text-base font-bold text-slate-900 group-hover:text-forest-ink transition-colors mb-1">
                    Find Cancer Centers
                  </h2>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    Locate verified NCI, NABH & JCI accredited oncology hospitals.
                  </p>
                </div>
                <div className="mt-4 flex items-center text-xs font-semibold text-forest-ink group-hover:text-forest-shadow">
                  <span>Explore Centers</span>
                  <ChevronRight className="w-3.5 h-3.5 ml-1 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>

              {/* Tile 2: Cost Calculator */}
              <Link
                to="/cost-calculator"
                onClick={() => trackCTA('estimate_cost', 'hero_tile', { page: '/cancer' })}
                className="group p-4 sm:p-5 rounded-2xl bg-white border border-slate-200/90 shadow-xs hover:shadow-md hover:border-emerald-600/40 hover:-translate-y-0.5 transition-all flex flex-col justify-between"
              >
                <div>
                  <div className="w-10 h-10 rounded-xl bg-emerald-50 text-forest-ink flex items-center justify-center mb-3 group-hover:bg-forest-ink group-hover:text-white transition-colors">
                    <Calculator className="w-5 h-5" />
                  </div>
                  <h2 className="text-base font-bold text-slate-900 group-hover:text-forest-ink transition-colors mb-1">
                    Cost Calculator
                  </h2>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    Compare realistic surgery, chemo & immunotherapy price ranges.
                  </p>
                </div>
                <div className="mt-4 flex items-center text-xs font-semibold text-forest-ink group-hover:text-forest-shadow">
                  <span>Calculate Cost</span>
                  <ChevronRight className="w-3.5 h-3.5 ml-1 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>

              {/* Tile 3: Second Opinion */}
              <Link
                to="/second-opinion"
                onClick={() => trackCTA('second_opinion', 'hero_tile', { page: '/cancer' })}
                className="group p-4 sm:p-5 rounded-2xl bg-white border border-slate-200/90 shadow-xs hover:shadow-md hover:border-emerald-600/40 hover:-translate-y-0.5 transition-all flex flex-col justify-between"
              >
                <div>
                  <div className="w-10 h-10 rounded-xl bg-emerald-50 text-forest-ink flex items-center justify-center mb-3 group-hover:bg-forest-ink group-hover:text-white transition-colors">
                    <Stethoscope className="w-5 h-5" />
                  </div>
                  <h2 className="text-base font-bold text-slate-900 group-hover:text-forest-ink transition-colors mb-1">
                    Second Opinion
                  </h2>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    Multidisciplinary tumor board review of biopsy & scans.
                  </p>
                </div>
                <div className="mt-4 flex items-center text-xs font-semibold text-forest-ink group-hover:text-forest-shadow">
                  <span>Request Review</span>
                  <ChevronRight className="w-3.5 h-3.5 ml-1 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>

              {/* Tile 4: Get Matched */}
              <Link
                to="/get-started"
                onClick={() => trackCTA('get_matched', 'hero_tile', { page: '/cancer' })}
                className="group p-4 sm:p-5 rounded-2xl bg-gradient-to-br from-forest-ink to-[#0c3313] text-white shadow-xs hover:shadow-md hover:-translate-y-0.5 transition-all flex flex-col justify-between"
              >
                <div>
                  <div className="w-10 h-10 rounded-xl bg-white/10 text-white flex items-center justify-center mb-3 group-hover:bg-white group-hover:text-forest-ink transition-colors">
                    <HeartPulse className="w-5 h-5" />
                  </div>
                  <h2 className="text-base font-bold text-white mb-1">
                    Care Matching
                  </h2>
                  <p className="text-xs text-white/80 leading-relaxed">
                    Connect 1-on-1 with an oncology patient care navigator.
                  </p>
                </div>
                <div className="mt-4 flex items-center text-xs font-semibold text-white group-hover:underline">
                  <span>Start Free Match</span>
                  <ChevronRight className="w-3.5 h-3.5 ml-1 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            </motion.div>
          </div>
        </section>

        {/* Directory Search & Interactive Category Filter Bar */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6 relative z-20">
          <div className="bg-white rounded-2xl border border-slate-200/90 shadow-md p-4 sm:p-6">
            <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4">
              
              {/* Search Input Box */}
              <div className="relative flex-1">
                <Search className="w-5 h-5 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search cancer by name, organ, or treatment (e.g., Breast, Lung, Surgery)..."
                  className="w-full pl-11 pr-10 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:bg-white focus:border-emerald-600 focus:ring-2 focus:ring-emerald-600/20 text-slate-900 placeholder-slate-400 text-sm transition outline-none"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1"
                    aria-label="Clear search"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Status counter */}
              <div className="flex items-center gap-2 text-xs font-semibold text-slate-500 whitespace-nowrap px-1">
                <Activity className="w-4 h-4 text-emerald-600" />
                <span>Showing {filteredCancers.length} of {cancerTypes.length} Guides</span>
              </div>
            </div>

            {/* Category Filter Chips */}
            <div className="flex items-center gap-2 overflow-x-auto pt-4 mt-2 border-t border-slate-100 no-scrollbar">
              {CATEGORIES.map(category => {
                const isActive = selectedCategory === category;
                return (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`px-3.5 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                      isActive
                        ? 'bg-forest-ink text-white shadow-xs'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200/80 hover:text-slate-900'
                    }`}
                  >
                    {category}
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        {/* Cancer Types Grid */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          
          {/* Section Header */}
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-2 mb-8">
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-emerald-700 mb-1">
                Evidence-Based Directory
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold font-serif text-slate-900 tracking-tight">
                {selectedCategory === 'All Types' ? 'All Cancer Treatment Guides' : `${selectedCategory} Guides`}
              </h2>
            </div>
            <p className="text-xs sm:text-sm text-slate-500">
              Click any guide for diagnostic pathways, staging breakdowns, and verified cost ranges.
            </p>
          </div>

          {/* Cards Grid or Empty State */}
          <AnimatePresence mode="popLayout">
            {filteredCancers.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredCancers.map((cancer, index) => (
                  <motion.div
                    key={cancer.slug}
                    layout
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.35, delay: index * 0.05 }}
                  >
                    <div className="group h-full bg-white rounded-2xl border border-slate-200/90 shadow-xs hover:shadow-lg hover:border-emerald-600/40 hover:-translate-y-1 transition-all duration-300 flex flex-col justify-between overflow-hidden">
                      
                      {/* Card Content Top */}
                      <div className="p-6">
                        
                        {/* Meta Tags Bar */}
                        <div className="flex items-center justify-between gap-2 mb-4">
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-800 border border-emerald-100">
                            {cancer.category}
                          </span>
                          <span className="text-[11px] font-medium text-slate-400">
                            {cancer.badge}
                          </span>
                        </div>

                        {/* Cancer Title with Stethoscope Icon */}
                        <div className="flex items-start gap-3 mb-3">
                          <div className="w-10 h-10 rounded-xl bg-emerald-50 text-forest-ink flex items-center justify-center flex-shrink-0 group-hover:bg-forest-ink group-hover:text-white transition-colors">
                            <Stethoscope className="w-5 h-5" />
                          </div>
                          <div>
                            <h3 className="text-xl font-bold font-serif text-slate-900 group-hover:text-forest-ink transition-colors leading-snug">
                              {cancer.name}
                            </h3>
                            <p className="text-xs text-slate-400 mt-0.5">
                              {cancer.cureRateBadge}
                            </p>
                          </div>
                        </div>

                        {/* Description */}
                        <p className="text-sm text-slate-600 leading-relaxed mb-5">
                          {cancer.shortDescription}
                        </p>

                        {/* Treatment Modality Pills */}
                        <div className="space-y-1.5 mb-2">
                          <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider block">
                            Key Treatment Modalities
                          </span>
                          <div className="flex flex-wrap gap-1.5">
                            {cancer.modalities.map((modality, mIdx) => (
                              <span
                                key={mIdx}
                                className="px-2 py-0.5 rounded-md text-[11px] font-medium bg-slate-50 text-slate-700 border border-slate-200/70"
                              >
                                {modality}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Card Footer Actions */}
                      <div className="p-6 pt-0 mt-auto border-t border-slate-100/80 bg-slate-50/50">
                        {/* Primary View Details Button */}
                        <button
                          onClick={() => {
                            trackCTA('view_details', 'cancer_card', { slug: cancer.slug });
                            navigate(`/cancer/${cancer.slug}`);
                          }}
                          className="w-full mt-4 flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-forest-ink hover:bg-forest-shadow text-white text-sm font-semibold transition shadow-xs group-hover:shadow"
                        >
                          <span>Explore Clinical Guide</span>
                          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </button>

                        {/* Secondary Actions */}
                        <div className="grid grid-cols-2 gap-2 mt-2 pt-2 border-t border-slate-200/60">
                          <button
                            onClick={() => {
                              trackCTA('estimate_cost', 'cancer_card', { slug: cancer.slug });
                              navigate('/cost-calculator');
                            }}
                            className="flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-lg text-xs font-semibold text-slate-600 hover:text-forest-ink hover:bg-emerald-50 transition"
                          >
                            <Calculator className="w-3.5 h-3.5 text-emerald-700" />
                            <span>Cost Guide</span>
                          </button>
                          <button
                            onClick={() => {
                              trackCTA('get_matched', 'cancer_card', { slug: cancer.slug });
                              navigate('/get-started');
                            }}
                            className="flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-lg text-xs font-semibold text-slate-600 hover:text-forest-ink hover:bg-emerald-50 transition"
                          >
                            <Building2 className="w-3.5 h-3.5 text-emerald-700" />
                            <span>Find Centers</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-16 bg-white rounded-2xl border border-slate-200/80 p-8"
              >
                <div className="w-12 h-12 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center mx-auto mb-4">
                  <Search className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold text-slate-800 mb-2">No cancer guides matched "{searchQuery}"</h3>
                <p className="text-sm text-slate-500 max-w-md mx-auto mb-6">
                  We cover 10+ standard and 28+ rare oncology classifications. Try checking your spelling or reach out to our navigation team.
                </p>
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setSelectedCategory('All Types');
                  }}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-forest-ink text-white text-xs sm:text-sm font-semibold hover:bg-forest-shadow transition"
                >
                  Reset Search & Filters
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Clinical Authority Banner: 3 Value Props */}
          <section className="mt-16 pt-12 border-t border-slate-200">
            <div className="text-center max-w-3xl mx-auto mb-10">
              <span className="text-xs font-bold uppercase tracking-wider text-emerald-700 mb-2 block">
                Standard of Care Integrity
              </span>
              <h2 className="text-2xl sm:text-3xl font-bold font-serif text-slate-900 tracking-tight mb-3">
                How ByOnco Guides Are Curated
              </h2>
              <p className="text-sm sm:text-base text-slate-600">
                Every guide follows verified clinical practice guidelines (NCCN, ESMO, ASCO) to give patients reliable information free from hospital bias.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="p-6 rounded-2xl bg-white border border-slate-200/90 shadow-xs">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 text-forest-ink flex items-center justify-center mb-4">
                  <BadgeCheck className="w-5 h-5" />
                </div>
                <h3 className="text-base font-bold text-slate-900 mb-2">
                  Evidence-Based Oncology
                </h3>
                <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                  Synthesized from peer-reviewed clinical trial results, standardized TNM staging classifications, and biomarker testing protocols.
                </p>
              </div>

              <div className="p-6 rounded-2xl bg-white border border-slate-200/90 shadow-xs">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 text-forest-ink flex items-center justify-center mb-4">
                  <Calculator className="w-5 h-5" />
                </div>
                <h3 className="text-base font-bold text-slate-900 mb-2">
                  Cross-Border Cost Clarity
                </h3>
                <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                  Transparent out-of-pocket benchmarks comparing surgery, radiation, and immunotherapy in leading Indian centers versus US hospitals.
                </p>
              </div>

              <div className="p-6 rounded-2xl bg-white border border-slate-200/90 shadow-xs">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 text-forest-ink flex items-center justify-center mb-4">
                  <Stethoscope className="w-5 h-5" />
                </div>
                <h3 className="text-base font-bold text-slate-900 mb-2">
                  Independent Second Opinions
                </h3>
                <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                  Verify biopsy pathology slides, CT/PET-CT DICOM scans, and surgical margins with specialized academic tumor board oncologists.
                </p>
              </div>
            </div>
          </section>

          {/* Reassuring High-Trust Bottom CTA Box */}
          <section className="mt-16">
            <div className="rounded-3xl bg-gradient-to-br from-forest-ink via-[#0d3b17] to-[#08240e] text-white p-8 sm:p-12 shadow-xl border border-emerald-900/40 relative overflow-hidden">
              
              {/* Radial Glow Accents */}
              <div className="absolute top-0 right-0 -mr-20 -mt-20 w-80 h-80 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
              <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-80 h-80 bg-emerald-400/10 rounded-full blur-3xl pointer-events-none" />

              <div className="max-w-3xl mx-auto text-center relative z-10">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-emerald-200 text-xs font-semibold mb-4 backdrop-blur-sm border border-white/10">
                  <Sparkles className="w-3.5 h-3.5 text-emerald-300" />
                  Personalized Cancer Care Navigation
                </div>

                <h2 className="text-2xl sm:text-4xl font-bold font-serif text-white tracking-tight mb-4">
                  Need Help Finding the Right Treatment Plan?
                </h2>
                
                <p className="text-sm sm:text-base text-emerald-100/90 max-w-2xl mx-auto leading-relaxed mb-8">
                  You don't have to navigate cancer alone. Connect with our medical navigation team to match with verified centers, verify insurance coverage, and consult top specialists.
                </p>

                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <Link
                    to="/get-started"
                    onClick={() => trackCTA('get_matched', 'bottom_banner', { page: '/cancer' })}
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full bg-white hover:bg-emerald-50 text-forest-ink font-semibold text-sm sm:text-base transition shadow-md hover:shadow-lg"
                  >
                    <span>Get Matched to Top Oncology Centers</span>
                    <ArrowRight className="w-4 h-4" />
                  </Link>

                  <Link
                    to="/second-opinion"
                    onClick={() => trackCTA('second_opinion', 'bottom_banner', { page: '/cancer' })}
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full border border-white/30 hover:border-white bg-white/5 hover:bg-white/10 text-white font-semibold text-sm sm:text-base transition"
                  >
                    <Stethoscope className="w-4 h-4" />
                    <span>Request Expert Second Opinion</span>
                  </Link>
                </div>

                {/* Trust Badges Row */}
                <div className="mt-8 pt-6 border-t border-white/10 flex flex-wrap items-center justify-center gap-6 text-xs text-emerald-200/80">
                  <span className="flex items-center gap-1.5">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    100% Confidential
                  </span>
                  <span className="flex items-center gap-1.5">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    Zero Obligation
                  </span>
                  <span className="flex items-center gap-1.5">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    Multidisciplinary Panel
                  </span>
                </div>
              </div>
            </div>
          </section>
        </main>

        {/* Global Botanical ByOnco Site Footer */}
        <Footer />
      </div>
    </>
  );
}
